import { TFunction } from 'i18next';
import { WidgetTemplatesKindEnum } from 'graphql-common';
import { FieldItem, Values } from '@lib/interfaces/form';
import {
  getAnnouncementFields,
  getExternalLinkFields,
  getWidgetDetailsFields,
  getVisibilityFields,
  getWidgetKindSelectorField,
  getPreviewButtonField,
} from 'routes/Home/utils/getWidgetTemplateSharedFields';

interface GetFormFieldsArgs {
  t: TFunction<'translation', undefined>;
  widgetKind: WidgetTemplatesKindEnum;
  onPreviewClick?: () => void;
  onFieldChange?: (values: Values) => void;
}

/**
 * Main function that returns form fields based on widget kind
 * Uses shared field definitions for consistency with create form
 */
export default function getWidgetTemplateFormFields({
  t,
  widgetKind,
  onPreviewClick,
  onFieldChange,
}: GetFormFieldsArgs): FieldItem[] {
  const config = { useDynamicVisibility: false, widgetKind };
  const kindConfig = { singleKind: widgetKind, onPreviewClick, onFieldChange };
  const fields: FieldItem[] = [];

  // Widget kind selector (single option, read-only for edit form)
  fields.push(getWidgetKindSelectorField(t, kindConfig));

  // Preview button
  fields.push(getPreviewButtonField(t, kindConfig));

  // Widget-specific fields based on kind
  switch (widgetKind) {
    case WidgetTemplatesKindEnum.Announcement:
      fields.push(...getAnnouncementFields(t, config));
      break;
    case WidgetTemplatesKindEnum.ExternalLink:
      fields.push(...getExternalLinkFields(t, config));
      break;
    default:
      // Other widget types may not have editable template settings
      break;
  }

  // Widget details (title, subtitle, icon)
  fields.push(...getWidgetDetailsFields(t, config));

  // Visibility toggle
  fields.push(...getVisibilityFields(t, config));

  return fields;
}
